import { Box, Button, Dialog, DialogContent, IconButton, InputLabel, MenuItem, Select, FormControl, Typography, TextField, Switch, FormControlLabel, FormGroup, Checkbox } from '@mui/material';
import { StaticTimePicker } from '@mui/x-date-pickers/StaticTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import React, { useEffect, useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
// Removed ArrowDownwardIcon in favor of horizontal layout
import { useTranslation } from 'react-i18next';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import toast from 'react-hot-toast';
import { api } from 'src/api/endpoints';
import SelectFilesModalScheduler from '../../scheduler/components/SelectFilesModalScheduler';
import SelectPlaylistModal from 'src/components/selectfilesmodal/SelectPlaylistModal';
import EditIcon from '@mui/icons-material/Edit';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { getEventPairInterval } from 'src/utils/getEventPairInterval';

const MIN_DIFF_MINUTES = 1;
const MIN_ONETIME_LEAD_MINUTES = 5;
const MIN_ONETIME_LEAD_MS = MIN_ONETIME_LEAD_MINUTES * 60 * 1000;

// New dialog supporting minute granularity without altering existing scheduler dialog
const AddEventDialogMinute = ({ open, onClose, presetDate, onSave, currentScheduler }) => {
  const { t } = useTranslation();
  // One-time event defaults: start 12:00, end 12:02 (minimum gap)
  const [onetimeTo, setOnetimeTo] = useState(new Date(new Date().setHours(12, MIN_DIFF_MINUTES, 0, 0)));
  const [onetimeFrom, setOnetimeFrom] = useState(new Date(new Date().setHours(12, 0, 0, 0)));
  const [selectedContent, setSelectedContent] = useState(null);
  const [selectFileControl, setSelectFileControl] = useState({ open: false, submitFn: null });
  const [selectPlaylistControl, setSelectPlaylistControl] = useState({ open: false, submitFn: null });
  const [timestamp, setTimestamp] = useState('WWW MMM DD RRRR **:**:**');
  const [timestampTo, setTimestampTo] = useState('WWW MMM DD RRRR **:**:**');
  const [recurringType, setRecurringType] = useState('def');
  const [repeat, setRepeat] = useState('daily');
  const [editContent, setEditContent] = useState({ name: '', id: '' });
  const [mode, setMode] = useState('add');
  const [onetimeContentType, setOnetimeContentType] = useState('def');
  const [onetimeStartError, setOnetimeStartError] = useState('');
  const [onetimeEndError, setOnetimeEndError] = useState('');
  const [endEnabled, setEndEnabled] = useState(false);
  const [startHourChosen, setStartHourChosen] = useState(false);
  const [endHourChosen, setEndHourChosen] = useState(false);
  const [startMinuteChosen, setStartMinuteChosen] = useState(false);
  const [is24HourFormat, setIs24HourFormat] = useState(true);

  const [excludedDays, setExcludedDays] = useState([
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
    'Sunday'
  ]);

  const months = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
  const daysWeek = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const daysOfMonth = ['01','02','03','04','05','06','07','08','09','10','11','12','13','14','15','16','17','18','19','20','21','22','23','24','25','26','27','28','29','30','31'];

  const pad = (value) => String(value).padStart(2, '0');
  const extractHour = (ts) => ts.split(' ')[4] === '**:**:**' ? '' : parseInt(ts.split(' ')[4].split(':')[0], 10);
  const extractMinute = (ts) => ts.split(' ')[4] === '**:**:**' ? '' : parseInt(ts.split(' ')[4].split(':')[1], 10);
  const isFiniteNumber = (value) => typeof value === 'number' && !Number.isNaN(value);
  const getTotalMinutes = (hour, minute) => hour * 60 + minute;
  const to12HourParts = (hour24) => {
    const meridiem = hour24 >= 12 ? 'PM' : 'AM';
    const hour12 = ((hour24 + 11) % 12) + 1;
    return { hour12, meridiem };
  };
  const to24Hour = (hour12, meridiem) => {
    const normalized = hour12 % 12;
    return meridiem === 'PM' ? normalized + 12 : normalized;
  };
  const setTimestampTime = (setter, hour, minute) => {
    if(!isFiniteNumber(hour)) return;
    const minuteValue = isFiniteNumber(minute) ? minute : 0;
    setter(`${pad(hour)}:${pad(minuteValue)}:00`, 'hour');
  };

  const resetAndClose = (close=false) => {
    setOnetimeTo(new Date(new Date().setHours(12, MIN_DIFF_MINUTES, 0, 0)));
    setOnetimeFrom(new Date(new Date().setHours(12, 0, 0, 0)));
    setTimestamp('WWW MMM DD RRRR **:**:**');
    setTimestampTo('WWW MMM DD RRRR **:**:**');
    setOnetimeContentType('def');
    setMode('add');
    setEditContent({ name: '', id: '' });
    setRepeat('daily');
    setRecurringType('def');
    setSelectFileControl({ open: false, submitFn: null });
    setSelectPlaylistControl({ open: false, submitFn: null });
    setSelectedContent(null);
    setOnetimeStartError('');
    setOnetimeEndError('');
    setEndEnabled(false);
    setStartHourChosen(false);
    setEndHourChosen(false);
    setStartMinuteChosen(false);
    setIs24HourFormat(true);
    setExcludedDays([
      'Monday',
      'Tuesday',
      'Wednesday',
      'Thursday',
      'Friday',
      'Saturday',
      'Sunday'
    ]);
    if(close) onClose();
  };

  useEffect(() => {
    console.log('Excluded days updated:', excludedDays);
  }, [excludedDays]);

  useEffect(() => {
    resetAndClose();
    const setupEdit = async () => {
      if(presetDate) {
        setMode('edit');
        try {
          const response = await api.get(`/scheduler/getEventPair/${presetDate}`);
          const eventStart = response.data?.pair?.find(p => p.type === 'start');
          const eventStop = response.data?.pair?.find(p => p.type === 'stop');
          if(getEventPairInterval(eventStart?.when) === 'once') {
            setRecurringType('onetime');
            // Parse timestamp parts without forcing UTC so hours stay unchanged
            const [startWeek, startMonthAbbr, startDay, startYear, startTime] = eventStart?.when.split(' ');
            const [stopWeek, stopMonthAbbr, stopDay, stopYear, stopTime] = eventStop?.when.split(' ');
            const startMonthIndex = months.findIndex(m => m.slice(0,3) === startMonthAbbr);
            const stopMonthIndex = months.findIndex(m => m.slice(0,3) === stopMonthAbbr);
            const [startHour, startMin] = startTime.split(':').map(n => parseInt(n,10));
            const [stopHour, stopMin] = stopTime.split(':').map(n => parseInt(n,10));
            setOnetimeFrom(new Date(parseInt(startYear,10), startMonthIndex, parseInt(startDay,10), startHour, startMin, 0, 0));
            setOnetimeTo(new Date(parseInt(stopYear,10), stopMonthIndex, parseInt(stopDay,10), stopHour, stopMin, 0, 0));
            setEndEnabled(true);
            setStartHourChosen(true);
            setStartMinuteChosen(true);
            setEndHourChosen(true);
          } else {
            setRecurringType('repeatable');
            const interval = getEventPairInterval(eventStart?.when);
            if(interval === 'day') setRepeat('daily');
            else if(interval === 'week') setRepeat('weekly');
            else if(interval === 'month') setRepeat('monthly');
            else if(interval === 'year') setRepeat('yearly');
            setTimestamp(eventStart?.when);
            setTimestampTo(eventStop?.when);
          }
          setEditContent({ name: eventStart?.playlist?.playlistName, id: eventStart?.playlist?._id });
        } catch(err) {
          toast.error(t('views.scheduler.errorGettingPair'));
        }
      }
    };
    setIs24HourFormat(localStorage.getItem("vcpOnetimeSchedulerTimeFormat") == '1' ? true : false)
    console.log(Boolean(localStorage.getItem("vcpOnetimeSchedulerTimeFormat")))
    setupEdit();
  }, [open]);

  const editTimestamp = (value, type) => {
    setTimestampTo('WWW MMM DD RRRR **:**:**');
    const parts = timestamp.split(' ');
    if(type === 'week') parts[0] = value;
    if(type === 'month') parts[1] = value;
    if(type === 'day') parts[2] = value;
    if(type === 'hour') parts[4] = value;
    setTimestamp(parts.join(' '));
  };

  const isSameCalendarDay = (a,b) => a?.getFullYear() === b?.getFullYear() && a?.getMonth() === b?.getMonth() && a?.getDate() === b?.getDate();

  const getOnetimeStartErrorMessage = () => t('views.scheduler.onetimePastHourError', {
    minutes: MIN_ONETIME_LEAD_MINUTES,
    defaultValue: `Start time must be at least ${MIN_ONETIME_LEAD_MINUTES} minutes in the future.`
  });

  const createExcludedDailyEvent = (isPlaylist) => {
      if(excludedDays.length > 0){
        excludedDays.forEach((d, index) => {
          const weekTimestampFrom = `${d.slice(0, 3)} MMM DD RRRR ${timestamp.split(' ')[4]}`
          const weekTimestampTo = `${d.slice(0, 3)} MMM DD RRRR ${timestampTo.split(' ')[4]}`
          const payload = {
            mode,
            pairId: presetDate,
            contentType: isPlaylist ? 'playlist' : 'object',
            contentId: selectedContent?._id || selectedContent?.id,
            whenStart: weekTimestampFrom,
            whenStop: weekTimestampTo
          };
          if(isPlaylist){
            payload.playlist = selectedContent;
            payload.playlistId = selectedContent?._id;
          }
          onSave(payload)
          if(index+1 == excludedDays.length){
            resetAndClose(true);
          }
        })
      }
  }

  const validateOnetimeStart = (candidate) => {
    if(!candidate) return false;
    const now = new Date();
    if(!isSameCalendarDay(candidate, now)) {
      setOnetimeStartError('');
      return true;
    }
    const minAllowed = Date.now() + MIN_ONETIME_LEAD_MS;
    if(candidate.getTime() < minAllowed) {
      setOnetimeStartError(getOnetimeStartErrorMessage());
      return false;
    }
    setOnetimeStartError('');
    return true;
  };

  const editTimestampTo = (value, type) => {
    const parts = timestampTo.split(' ');
    if(type === 'week') parts[0] = value;
    if(type === 'month') parts[1] = value;
    if(type === 'day') parts[2] = value;
    if(type === 'hour') parts[4] = value;
    setTimestampTo(parts.join(' '));
  };

  const canSelectContent = () => {
    if(recurringType !== 'repeatable') return false;
    const startTimeRaw = timestamp.split(' ')[4];
    const stopTimeRaw = timestampTo.split(' ')[4];
    if(startTimeRaw === '**:**:**' || stopTimeRaw === '**:**:**') return false;

    if(repeat === 'weekly') {
      if(timestamp.split(' ')[0] === 'WWW' || timestampTo.split(' ')[0] === 'WWW') return false;
    }

    if(repeat === 'monthly') {
      if(timestamp.split(' ')[2] === 'DD' || timestampTo.split(' ')[2] === 'DD') return false;
    }

    if(repeat === 'yearly') {
      if(timestamp.split(' ')[1] === 'MMM' || timestampTo.split(' ')[1] === 'MMM') return false;
      if(timestamp.split(' ')[2] === 'DD' || timestampTo.split(' ')[2] === 'DD') return false;
    }

    return true;
  };

  const handleSaveEvent = () => {
    if(recurringType === 'onetime') {
      const diffMinutes = (onetimeTo.getTime() - onetimeFrom.getTime()) / (1000 * 60);
      if(diffMinutes < MIN_DIFF_MINUTES) {
        toast.error(t('views.scheduler.minHourDiff'));
        return;
      }
    } else {
      const startTimeRaw = timestamp.split(' ')[4];
      const stopTimeRaw = timestampTo.split(' ')[4];
      if(startTimeRaw === '**:**:**' || stopTimeRaw === '**:**:**') {
        toast.error(t('views.scheduler.minHourDiff'));
        return;
      }

      const parseMinutes = (time) => {
        const [hh, mm] = time.split(':');
        return parseInt(hh, 10) * 60 + parseInt(mm, 10);
      };

      if(repeat === 'weekly') {
        const startDay = timestamp.split(' ')[0];
        const stopDay = timestampTo.split(' ')[0];
        if(startDay === 'WWW' || stopDay === 'WWW') {
          toast.error(t('views.scheduler.minHourDiff'));
          return;
        }
        const startIdx = daysWeek.findIndex(d => d.slice(0,3) === startDay);
        const stopIdx = daysWeek.findIndex(d => d.slice(0,3) === stopDay);
        if(startIdx === -1 || stopIdx === -1) {
          toast.error(t('views.scheduler.minHourDiff'));
          return;
        }
        if(stopIdx < startIdx) {
          toast.error(t('views.scheduler.minHourDiff'));
          return;
        }
        if(stopIdx === startIdx) {
          if((parseMinutes(stopTimeRaw) - parseMinutes(startTimeRaw)) < MIN_DIFF_MINUTES) {
            toast.error(t('views.scheduler.minHourDiff'));
            return;
          }
        }
      } else if(repeat === 'monthly') {
        const startDay = timestamp.split(' ')[2];
        const stopDay = timestampTo.split(' ')[2];
        if(startDay === 'DD' || stopDay === 'DD') {
          toast.error(t('views.scheduler.minHourDiff'));
          return;
        }
        const startIdx = daysOfMonth.findIndex(d => d === startDay);
        const stopIdx = daysOfMonth.findIndex(d => d === stopDay);
        if(startIdx === -1 || stopIdx === -1) {
          toast.error(t('views.scheduler.minHourDiff'));
          return;
        }
        if(stopIdx < startIdx) {
          toast.error(t('views.scheduler.minHourDiff'));
          return;
        }
        if(stopIdx === startIdx) {
          if((parseMinutes(stopTimeRaw) - parseMinutes(startTimeRaw)) < MIN_DIFF_MINUTES) {
            toast.error(t('views.scheduler.minHourDiff'));
            return;
          }
        }
      } else if(repeat === 'yearly') {
        const startMonthAbbr = timestamp.split(' ')[1];
        const stopMonthAbbr = timestampTo.split(' ')[1];
        const startDay = timestamp.split(' ')[2];
        const stopDay = timestampTo.split(' ')[2];
        if(startMonthAbbr === 'MMM' || stopMonthAbbr === 'MMM' || startDay === 'DD' || stopDay === 'DD') {
          toast.error(t('views.scheduler.minHourDiff'));
          return;
        }
        const startMonthIdx = months.findIndex(m => m.slice(0,3) === startMonthAbbr);
        const stopMonthIdx = months.findIndex(m => m.slice(0,3) === stopMonthAbbr);
        if(startMonthIdx === -1 || stopMonthIdx === -1) {
          toast.error(t('views.scheduler.minHourDiff'));
          return;
        }
        const startDayNum = parseInt(startDay, 10);
        const stopDayNum = parseInt(stopDay, 10);
        if(Number.isNaN(startDayNum) || Number.isNaN(stopDayNum)) {
          toast.error(t('views.scheduler.minHourDiff'));
          return;
        }
        const startDate = new Date(2025, startMonthIdx, parseInt(startDay,10), parseInt(startTimeRaw.split(':')[0],10), parseInt(startTimeRaw.split(':')[1],10));
        const stopDate = new Date(2025, stopMonthIdx, parseInt(stopDay,10), parseInt(stopTimeRaw.split(':')[0],10), parseInt(stopTimeRaw.split(':')[1],10));
        if((stopDate.getTime() - startDate.getTime()) < MIN_DIFF_MINUTES * 60000) {
          toast.error(t('views.scheduler.minHourDiff'));
          return;
        }
      } else {
        const startMinutes = parseMinutes(startTimeRaw);
        const stopMinutes = parseMinutes(stopTimeRaw);
        if((stopMinutes - startMinutes) < MIN_DIFF_MINUTES) {
          toast.error(t('views.scheduler.minHourDiff'));
          return;
        }
      }
    }
    const isPlaylist = !!selectedContent?.playlistName;
    const payload = {
      mode,
      pairId: presetDate,
      contentType: isPlaylist ? 'playlist' : 'object',
      contentId: selectedContent?._id || selectedContent?.id,
      whenStart: recurringType === 'onetime'
        ? `WWW ${months[onetimeFrom.getMonth()].slice(0,3)} ${String(onetimeFrom.getDate()).padStart(2,'0')} ${onetimeFrom.getFullYear()} ${String(onetimeFrom.getHours()).padStart(2,'0')}:${String(onetimeFrom.getMinutes()).padStart(2,'0')}:00`
        : timestamp,
      whenStop: recurringType === 'onetime'
        ? `WWW ${months[onetimeTo.getMonth()].slice(0,3)} ${String(onetimeTo.getDate()).padStart(2,'0')} ${onetimeTo.getFullYear()} ${String(onetimeTo.getHours()).padStart(2,'0')}:${String(onetimeTo.getMinutes()).padStart(2,'0')}:00`
        : timestampTo
    };
    if (isPlaylist) {
      payload.playlist = selectedContent;
      payload.playlistId = selectedContent?._id;
    }
    console.log('[AddEventDialogMinute] Payload:', payload);
    if(repeat == 'daily' && excludedDays.length > 1 && excludedDays.length < 7){
      createExcludedDailyEvent(isPlaylist)
    }else {
      onSave(payload);
      resetAndClose(true);
    }
  };

  useEffect(() => {
    if(onetimeContentType == 'object'){
      setSelectFileControl({ submitFn: setSelectedContent, open: true })      
    }else if(onetimeContentType === 'playlist'){
      setSelectPlaylistControl({ submitFn: setSelectedContent, open: true })
    }
  }, [onetimeContentType])

  const ContentSelect = ({isRepeatable}) => (
    <>
      {editContent.id ? (
        <>
          <Box sx={{ display:'flex', justifyContent:'center', alignItems:'center' }}>
            <Typography variant='subtitle1'>{editContent.name.length > 20 ? `${editContent.name.slice(0,20)}...` : editContent.name}</Typography>
            <IconButton onClick={() => setEditContent({ name:'', id:'' })}><EditIcon /></IconButton>
          </Box>
          <Box sx={{ display:'flex', justifyContent:'center' }}>
            {isRepeatable ? (
              <Button variant='contained' size='sm' onClick={handleSaveEvent}>{t('views.scheduler.save')}</Button>
            ) : (
              <Button variant='contained' size='sm' onClick={handleSaveEvent} disabled={!!(onetimeStartError || onetimeEndError)}>{t('views.scheduler.save')}</Button>
            )}
          </Box>
        </>
      ) : (
        <>
          <Box sx={{ display:'flex', justifyContent:'center', gap:'10px' }}>
            <FormControl sx={{ minWidth:150 }} size='small'>
              <Select value={onetimeContentType} onChange={(e) => setOnetimeContentType(e.target.value)}>
                <MenuItem value='def'><em>{t('views.scheduler.contentType')}</em></MenuItem>
                <MenuItem value='object'>{t('views.scheduler.object')}</MenuItem>
                <MenuItem value='playlist'>{t('views.scheduler.playlist')}</MenuItem>
              </Select>
            </FormControl>
            {onetimeContentType === 'object' && (
              <Button variant='outlined' onClick={() => setSelectFileControl({ submitFn: setSelectedContent, open: true })}>{t('views.scheduler.selectObject')}</Button>
            )}
            {onetimeContentType === 'playlist' && (
              <Button variant='outlined' onClick={() => setSelectPlaylistControl({ submitFn: setSelectedContent, open: true })}>{t('views.scheduler.selectPlaylist')}</Button>
            )}
          </Box>
          {selectedContent && (
            <>
              {selectedContent.playlistName && (
                <Box sx={{ display:'flex', justifyContent:'center', my:1 }}>
                  <Typography variant='body2' sx={{ opacity:0.7 }}>{t('views.scheduler.playlist')}: {selectedContent.playlistName}</Typography>
                </Box>
              )}
              {selectedContent.name && !selectedContent.playlistName && (
                <Box sx={{ display:'flex', justifyContent:'center', my:1 }}>
                  <Typography variant='body2' sx={{ opacity:0.7 }}>{t('views.scheduler.object')}: {selectedContent.name}</Typography>
                </Box>
              )}
              {/* <Box>
                <Typography>Select what do display between events</Typography>
                
              </Box> */}
              <Box sx={{ display:'flex', justifyContent:'center' }}>
                {isRepeatable ? (
                  <Button variant='contained' size='sm' onClick={handleSaveEvent}>{mode === 'add' ? t('views.scheduler.schedule') : t('views.scheduler.save')}</Button>
                ) : (
                  <Button variant='contained' size='sm' onClick={handleSaveEvent} disabled={!!(onetimeStartError || onetimeEndError || !endHourChosen)}>{mode === 'add' ? t('views.scheduler.schedule') : t('views.scheduler.save')}</Button>
                )}
              </Box>
            </>
          )}
        </>
      )}
    </>
  );

  const timestampParts = timestamp.split(' ');
  const timestampToParts = timestampTo.split(' ');
  const startWeekValue = timestampParts[0];
  const startMonthValue = timestampParts[1];
  const startDayValue = timestampParts[2];
  const stopWeekValue = timestampToParts[0];
  const stopMonthValue = timestampToParts[1];
  const stopDayValue = timestampToParts[2];
  const startHourValue = extractHour(timestamp);
  const startMinuteValue = extractMinute(timestamp);
  const stopHourValue = extractHour(timestampTo);
  const stopMinuteValue = extractMinute(timestampTo);
  const startHourDisplayValue = isFiniteNumber(startHourValue)
    ? (is24HourFormat ? startHourValue : to12HourParts(startHourValue).hour12)
    : '';
  const stopHourDisplayValue = isFiniteNumber(stopHourValue)
    ? (is24HourFormat ? stopHourValue : to12HourParts(stopHourValue).hour12)
    : '';
  const startMeridiemValue = isFiniteNumber(startHourValue) ? to12HourParts(startHourValue).meridiem : 'AM';
  const stopMeridiemValue = isFiniteNumber(stopHourValue) ? to12HourParts(stopHourValue).meridiem : 'AM';
  const startWeekIndex = daysWeek.findIndex(d => d.slice(0,3) === startWeekValue);
  const weeklyStopOptions = startWeekIndex === -1 ? daysWeek : daysWeek.slice(startWeekIndex);
  const startMonthIndex = months.findIndex(m => m.slice(0,3) === startMonthValue);
  const yearlyStopMonthOptions = startMonthIndex === -1 ? months : months.slice(startMonthIndex);
  const startDayIndex = daysOfMonth.findIndex(d => d === startDayValue);
  const monthlyStopOptions = startDayIndex === -1 ? daysOfMonth : daysOfMonth.slice(startDayIndex);
  const yearlySameMonth = startMonthValue !== 'MMM' && startMonthValue === stopMonthValue;
  const yearlyDayStartIndex = startDayValue === 'DD' ? -1 : daysOfMonth.findIndex(d => d === startDayValue);
  const yearlyStopDayOptions = yearlySameMonth && yearlyDayStartIndex !== -1
    ? daysOfMonth.slice(yearlyDayStartIndex)
    : daysOfMonth;

  return (
    <>
      <SelectPlaylistModal open={selectPlaylistControl.open} onClose={() => setSelectPlaylistControl({ ...selectPlaylistControl, open:false })} onSelectPlaylist={selectPlaylistControl.submitFn} />
      <SelectFilesModalScheduler open={selectFileControl.open} setOpenSelectFiles={() => setSelectFileControl({ ...selectFileControl, open:false })} onSaveFiles={selectFileControl.submitFn} />
      <Dialog open={open} onClose={() => resetAndClose(true)} fullWidth maxWidth='md'>
        <DialogContent>
          <Box sx={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:'20px' }}>
            <Box sx={{ display:'flex', alignItems:'center', gap:'10px' }}>
              <CalendarMonthIcon />
              <Typography variant='h6' sx={{
                color: currentScheduler?.color
              }}>{currentScheduler?.name}</Typography>
            </Box>
            <IconButton onClick={() => resetAndClose(true)}><CloseIcon /></IconButton>
          </Box>
          <Box sx={{ display:'flex', flexDirection:'column', gap:'20px', my:2 }}>
            <Box sx={{ display:'flex', alignItems:'center', justifyContent:'center', gap:'20px' }}>
              <Typography>{t('views.scheduler.type')}:</Typography>
              <FormControl sx={{ minWidth:150 }} size='small'>
                <Select value={recurringType} onChange={(e) => setRecurringType(e.target.value)}>
                  <MenuItem value='def'><em>{t('views.scheduler.chooseType')}</em></MenuItem>
                  <MenuItem value='onetime'>{t('views.scheduler.onetime')}</MenuItem>
                  <MenuItem value='repeatable'>{t('views.scheduler.repeatable')}</MenuItem>
                </Select>
              </FormControl>
            </Box>
            {recurringType === 'onetime' && (
              <>
                <Box sx={{ display:'flex', flexDirection:'column', alignItems:'center', gap:'12px', minHeight:'290px' }}>
                  
                  {/* <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={is24HourFormat}
                          onChange={(e) => setIs24HourFormat(e.target.checked)}
                        />
                      }
                      label={is24HourFormat ? '24h' : '12h'}
                    />
                  </Box> */}

                  <Box sx={{ display:'flex', alignItems:'flex-start', justifyContent:'center', gap:'24px', flexWrap:'wrap' }}>
                    <Box sx={{ display:'flex', flexDirection:'column', alignItems:'center', gap:'12px' }}>
                      <DatePicker selected={onetimeFrom} onChange={(date) => {
                        // Force time to 12:00 on date change and keep end at minimum gap
                        const startFixed = new Date(date.getFullYear(), date.getMonth(), date.getDate(), 12, 0, 0, 0);
                        const endFixed = new Date(date.getFullYear(), date.getMonth(), date.getDate(), 12, MIN_DIFF_MINUTES, 0, 0);
                        setOnetimeFrom(startFixed);
                        setOnetimeTo(endFixed);
                        // Only validate when both hour and minute are selected; after date change reset selection
                        setOnetimeStartError('');
                        setStartHourChosen(false);
                        setStartMinuteChosen(false);
                        setEndEnabled(true);
                        setEndHourChosen(true);
                      }} selectsStart startDate={onetimeFrom} endDate={onetimeTo} minDate={new Date()} />
                      <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <StaticTimePicker
                          value={onetimeFrom}
                          ampm={!is24HourFormat}
                          onChange={(newValue) => {
                            if(!newValue || isNaN(newValue.getTime())) return;
                            const newFrom = new Date(onetimeFrom.getFullYear(), onetimeFrom.getMonth(), onetimeFrom.getDate(), newValue.getHours(), newValue.getMinutes());
                            if(!validateOnetimeStart(newFrom)) return;
                            setOnetimeFrom(newFrom);
                            if(onetimeTo.getTime() <= newFrom.getTime()) {
                              const newTo = new Date(newFrom.getTime() + 30*60*1000);
                              setOnetimeTo(newTo);
                            }
                            setStartHourChosen(true);
                            setStartMinuteChosen(true);
                            setEndEnabled(true);
                            setEndHourChosen(true);
                          }}
                          slotProps={{
                            actionBar: { actions: [] }
                          }}
                          sx={{
                            border: !!onetimeStartError ? '1px solid #d32f2f' : '1px solid rgba(0, 0, 0, 0.23)',
                            borderRadius: 1
                          }}
                        />
                      </LocalizationProvider>
                    </Box>
                    <ArrowForwardIcon />
                    <Box sx={{ display:'flex', flexDirection:'column', alignItems:'center', gap:'12px' }}>
                      <DatePicker selected={onetimeTo} disabled={!endEnabled} onChange={(date) => {
                        // When end date manually changed, keep its time offset by the minimum gap unless same day needs adjustment
                        const endFixed = new Date(date.getFullYear(), date.getMonth(), date.getDate(), 12, MIN_DIFF_MINUTES, 0, 0);
                        // If end date equals start date and would make diff smaller than allowed, bump by the minimum gap from start
                        if(endFixed.toDateString() === onetimeFrom.toDateString()) {
                          const diffMinutes = (endFixed.getTime() - onetimeFrom.getTime()) / 60000;
                          if(diffMinutes < MIN_DIFF_MINUTES) {
                            const adjusted = new Date(onetimeFrom.getFullYear(), onetimeFrom.getMonth(), onetimeFrom.getDate(), onetimeFrom.getHours(), onetimeFrom.getMinutes() + MIN_DIFF_MINUTES, 0, 0);
                            setOnetimeTo(adjusted);
                            setEndHourChosen(true);
                            return;
                          }
                        }
                        setOnetimeTo(endFixed);
                        setEndHourChosen(true);
                      }} selectsEnd startDate={onetimeFrom} endDate={onetimeTo} minDate={onetimeFrom} />
                      <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <StaticTimePicker
                          value={onetimeTo}
                          disabled={!endEnabled}
                          ampm={!is24HourFormat}
                          onChange={(newValue) => {
                            if(!newValue || isNaN(newValue.getTime())) return;
                            const candidate = new Date(onetimeTo.getFullYear(), onetimeTo.getMonth(), onetimeTo.getDate(), newValue.getHours(), newValue.getMinutes());
                            const diffMinutes = (candidate.getTime() - onetimeFrom.getTime()) / 60000;
                            if(diffMinutes < MIN_DIFF_MINUTES) {
                              setOnetimeEndError(t('views.scheduler.minHourDiff'));
                              return;
                            }
                            setOnetimeEndError('');
                            setOnetimeTo(candidate);
                            setEndHourChosen(true);
                          }}
                          slotProps={{
                            actionBar: { actions: [] }
                          }}
                          sx={{
                            border: '1px solid rgba(0, 0, 0, 0.23)',
                            borderRadius: 1
                          }}
                        />
                      </LocalizationProvider>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={is24HourFormat}
                          onChange={(e) => {
                            setIs24HourFormat(e.target.checked)
                            localStorage.setItem("vcpOnetimeSchedulerTimeFormat", e.target.checked ? "1" : '0')
                          }}
                        />
                      }
                      label={is24HourFormat ? '24h' : '12h'}
                    />
                  </Box>                  
                  {(onetimeStartError || onetimeEndError) && (
                    <Typography variant='caption' color='error' sx={{ mt: 1 }}>
                      {onetimeStartError || onetimeEndError}
                    </Typography>
                  )}
                </Box>
                {onetimeFrom && onetimeTo && <ContentSelect />}
              </>
            )}
            {recurringType === 'repeatable' && (
              <>
                <Box sx={{ width: '100%', display:'flex', alignItems:'flex-start', justifyContent:'space-between', gap:'20px' }}>
                  <Box sx={{ display:'flex', alignItems:'center', justifyContent:'center', gap:'20px', flex: 1 }}>
                    <Typography>{t('views.scheduler.repeat')}:</Typography>
                    <FormControl sx={{ minWidth:200 }} size='small'>
                      <Select value={repeat} onChange={(e) => { setRepeat(e.target.value); setTimestamp('WWW MMM DD RRRR **:**:**'); setTimestampTo('WWW MMM DD RRRR **:**:**'); }}>
                        <MenuItem value='daily'>{t('views.scheduler.daily')}</MenuItem>
                        <MenuItem value='weekly'>{t('views.scheduler.weekly')}</MenuItem>
                        <MenuItem value='monthly'>{t('views.scheduler.monthly')}</MenuItem>
                        <MenuItem value='yearly'>{t('views.scheduler.yearly')}</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>
                  <FormControlLabel
                    sx={{ mr: 0 }}
                    control={
                      <Switch
                        checked={is24HourFormat}
                        onChange={(e) => {
                          setIs24HourFormat(e.target.checked)
                          localStorage.setItem("vcpOnetimeSchedulerTimeFormat", e.target.checked ? "1" : '0')
                        }}
                      />
                    }
                    label={is24HourFormat ? '24h' : '12h'}
                  />
                </Box>
                {repeat === 'daily' && (
                  <>
                    <Box sx={{ display:'flex', justifyContent:'center', gap:'40px', alignItems:'center', flexWrap:'wrap' }}>
                      <Box sx={{
                        width: "100%",
                        display: "flex",
                        justifyContent: "center",
                        gap: "20px"
                      }}> 
                        <FormGroup sx={{
                          flexDirection: 'row', justifyContent: 'center'
                        }}>
                          <FormControlLabel sx={{
                            flexDirection: 'column', justifyContent: 'center'
                          }} control={<Checkbox checked={excludedDays.includes('Monday')} onChange={(e) => {
                            !e.target.checked
                              ? setExcludedDays(excludedDays.filter(d => d !== 'Monday'))
                              : setExcludedDays([...excludedDays, 'Monday']);
                          }} />} label={t('views.scheduler.weekdays.monday')} />
                          <FormControlLabel sx={{
                            flexDirection: 'column', justifyContent: 'center'
                          }} control={<Checkbox checked={excludedDays.includes('Tuesday')} onChange={(e) => {
                            !e.target.checked
                              ? setExcludedDays(excludedDays.filter(d => d !== 'Tuesday'))
                              : setExcludedDays([...excludedDays, 'Tuesday']);
                          }} />} label={t('views.scheduler.weekdays.tuesday')} />
                          <FormControlLabel sx={{
                            flexDirection: 'column', justifyContent: 'center'
                          }} control={<Checkbox checked={excludedDays.includes('Wednesday')} onChange={(e) => {
                            !e.target.checked
                              ? setExcludedDays(excludedDays.filter(d => d !== 'Wednesday'))
                              : setExcludedDays([...excludedDays, 'Wednesday']);
                          }} />} label={t('views.scheduler.weekdays.wednesday')} />
                          <FormControlLabel sx={{
                            flexDirection: 'column', justifyContent: 'center'
                          }} control={<Checkbox checked={excludedDays.includes('Thursday')} onChange={(e) => {
                            !e.target.checked
                              ? setExcludedDays(excludedDays.filter(d => d !== 'Thursday'))
                              : setExcludedDays([...excludedDays, 'Thursday']);
                          }} />} label={t('views.scheduler.weekdays.thursday')} />
                          <FormControlLabel sx={{
                            flexDirection: 'column', justifyContent: 'center'
                          }} control={<Checkbox checked={excludedDays.includes('Friday')} onChange={(e) => {
                            !e.target.checked
                              ? setExcludedDays(excludedDays.filter(d => d !== 'Friday'))
                              : setExcludedDays([...excludedDays, 'Friday']);
                          }} />} label={t('views.scheduler.weekdays.friday')} />
                          <FormControlLabel sx={{
                            flexDirection: 'column', justifyContent: 'center'
                          }} control={<Checkbox checked={excludedDays.includes('Saturday')} onChange={(e) => {
                            !e.target.checked
                              ? setExcludedDays(excludedDays.filter(d => d !== 'Saturday'))
                              : setExcludedDays([...excludedDays, 'Saturday']);
                          }} />} label={t('views.scheduler.weekdays.saturday')} />
                          <FormControlLabel sx={{
                            flexDirection: 'column', justifyContent: 'center'
                          }} control={<Checkbox checked={excludedDays.includes('Sunday')} onChange={(e) => {
                            !e.target.checked
                              ? setExcludedDays(excludedDays.filter(d => d !== 'Sunday'))
                              : setExcludedDays([...excludedDays, 'Sunday']);
                          }} />} label={t('views.scheduler.weekdays.sunday')} />
                        </FormGroup>
                      </Box>
                      <Box sx={{ display:'flex', flexDirection:'column', alignItems:'center', gap:'10px' }}>
                        <Typography variant='caption'>{t('views.scheduler.from')}</Typography>
                        <Box sx={{ display:'flex', gap:'12px', alignItems:'center' }}>
                          <FormControl size='small' sx={{ minWidth:90 }}>
                            <InputLabel id='dailyStartHour'>{t('views.scheduler.hour')}</InputLabel>
                            <Select
                              labelId='dailyStartHour'
                              label={t('views.scheduler.hour')}
                              value={startHourDisplayValue}
                              onChange={(e) => {
                                const hourVal = parseInt(e.target.value, 10);
                                const resolvedHour = is24HourFormat ? hourVal : to24Hour(hourVal, startMeridiemValue);
                                const minuteVal = isFiniteNumber(startMinuteValue) ? startMinuteValue : 0;
                                setTimestampTime(editTimestamp, resolvedHour, minuteVal);
                              }}
                            >
                              {is24HourFormat
                                ? Array.from({ length:24 }, (_, i) => (
                                    <MenuItem key={i} value={i}>{String(i).padStart(2,'0')}</MenuItem>
                                  ))
                                : Array.from({ length:12 }, (_, i) => (
                                    <MenuItem key={i + 1} value={i + 1}>{String(i + 1).padStart(2,'0')}</MenuItem>
                                  ))}
                            </Select>
                          </FormControl>
                          <Typography>:</Typography>
                          <FormControl size='small' sx={{ minWidth:90 }}>
                            <InputLabel id='dailyStartMinute'>{t('views.scheduler.minute')}</InputLabel>
                            <Select
                              labelId='dailyStartMinute'
                              label={t('views.scheduler.minute')}
                              value={isFiniteNumber(startMinuteValue) ? startMinuteValue : ''}
                              onChange={(e) => {
                                const minuteVal = parseInt(e.target.value, 10);
                                const hourVal = isFiniteNumber(startHourValue) ? startHourValue : 0;
                                setTimestampTime(editTimestamp, hourVal, minuteVal);
                              }}
                              disabled={!isFiniteNumber(startHourValue)}
                            >
                              {Array.from({ length:60 }, (_, i) => (
                                <MenuItem key={i} value={i}>{String(i).padStart(2,'0')}</MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                          {!is24HourFormat && (
                            <FormControl size='small' sx={{ minWidth:90 }}>
                              <InputLabel id='dailyStartMeridiem'>AM/PM</InputLabel>
                              <Select
                                labelId='dailyStartMeridiem'
                                label='AM/PM'
                                value={startMeridiemValue}
                                onChange={(e) => {
                                  if(!isFiniteNumber(startHourDisplayValue)) return;
                                  const resolvedHour = to24Hour(startHourDisplayValue, e.target.value);
                                  const minuteVal = isFiniteNumber(startMinuteValue) ? startMinuteValue : 0;
                                  setTimestampTime(editTimestamp, resolvedHour, minuteVal);
                                }}
                                disabled={!isFiniteNumber(startHourDisplayValue)}
                              >
                                <MenuItem value='AM'>AM</MenuItem>
                                <MenuItem value='PM'>PM</MenuItem>
                              </Select>
                            </FormControl>
                          )}
                        </Box>
                      </Box>
                      <ArrowForwardIcon />
                      <Box sx={{ display:'flex', flexDirection:'column', alignItems:'center', gap:'10px' }}>
                        <Typography variant='caption'>{t('views.scheduler.to')}</Typography>
                        <Box sx={{ display:'flex', gap:'12px', alignItems:'center' }}>
                          <FormControl size='small' sx={{ minWidth:90 }}>
                            <InputLabel id='dailyStopHour'>{t('views.scheduler.hour')}</InputLabel>
                            <Select
                              labelId='dailyStopHour'
                              label={t('views.scheduler.hour')}
                              value={stopHourDisplayValue}
                              onChange={(e) => {
                                const hourVal = parseInt(e.target.value, 10);
                                const resolvedHour = is24HourFormat ? hourVal : to24Hour(hourVal, stopMeridiemValue);
                                const minuteVal = isFiniteNumber(stopMinuteValue) ? stopMinuteValue : 0;
                                setTimestampTime(editTimestampTo, resolvedHour, minuteVal);
                              }}
                              disabled={!isFiniteNumber(startHourValue)}
                            >
                              {is24HourFormat
                                ? Array.from({ length:24 }, (_, i) => (
                                    <MenuItem key={i} value={i}>{String(i).padStart(2,'0')}</MenuItem>
                                  ))
                                : Array.from({ length:12 }, (_, i) => (
                                    <MenuItem key={i + 1} value={i + 1}>{String(i + 1).padStart(2,'0')}</MenuItem>
                                  ))}
                            </Select>
                          </FormControl>
                          <Typography>:</Typography>
                          <FormControl size='small' sx={{ minWidth:90 }}>
                            <InputLabel id='dailyStopMinute'>{t('views.scheduler.minute')}</InputLabel>
                            <Select
                              labelId='dailyStopMinute'
                              label={t('views.scheduler.minute')}
                              value={isFiniteNumber(stopMinuteValue) ? stopMinuteValue : ''}
                              onChange={(e) => {
                                if(!isFiniteNumber(stopHourValue)) return;
                                const minuteVal = parseInt(e.target.value, 10);
                                if(isFiniteNumber(startHourValue) && isFiniteNumber(startMinuteValue)) {
                                  const startTotal = getTotalMinutes(startHourValue, startMinuteValue);
                                  const candidate = getTotalMinutes(stopHourValue, minuteVal);
                                  if((candidate - startTotal) < MIN_DIFF_MINUTES) {
                                    toast.error(t('views.scheduler.minHourDiff'));
                                    return;
                                  }
                                }
                                setTimestampTime(editTimestampTo, stopHourValue, minuteVal);
                              }}
                              disabled={!isFiniteNumber(stopHourValue)}
                            >
                              {Array.from({ length:60 }, (_, i) => (
                                <MenuItem key={i} value={i}>{String(i).padStart(2,'0')}</MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                          {!is24HourFormat && (
                            <FormControl size='small' sx={{ minWidth:90 }}>
                              <InputLabel id='dailyStopMeridiem'>AM/PM</InputLabel>
                              <Select
                                labelId='dailyStopMeridiem'
                                label='AM/PM'
                                value={stopMeridiemValue}
                                onChange={(e) => {
                                  if(!isFiniteNumber(stopHourDisplayValue)) return;
                                  const resolvedHour = to24Hour(stopHourDisplayValue, e.target.value);
                                  const minuteVal = isFiniteNumber(stopMinuteValue) ? stopMinuteValue : 0;
                                  if(isFiniteNumber(startHourValue) && isFiniteNumber(startMinuteValue)) {
                                    const startTotal = getTotalMinutes(startHourValue, startMinuteValue);
                                    const candidate = getTotalMinutes(resolvedHour, minuteVal);
                                    if((candidate - startTotal) < MIN_DIFF_MINUTES) {
                                      toast.error(t('views.scheduler.minHourDiff'));
                                      return;
                                    }
                                  }
                                  setTimestampTime(editTimestampTo, resolvedHour, minuteVal);
                                }}
                                disabled={!isFiniteNumber(stopHourDisplayValue)}
                              >
                                <MenuItem value='AM'>AM</MenuItem>
                                <MenuItem value='PM'>PM</MenuItem>
                              </Select>
                            </FormControl>
                          )}
                        </Box>
                      </Box>
                    </Box>
                    {canSelectContent() && (
                      <Box sx={{ mt:3 }}>
                        <ContentSelect isRepeatable={true} />
                      </Box>
                    )}
                  </>
                )}
                {repeat === 'weekly' && (
                  <>
                    <Box sx={{ display:'flex', justifyContent:'center', gap:'30px', flexWrap:'wrap' }}>
                      <Box sx={{ display:'flex', flexDirection:'column', gap:'12px', alignItems:'center' }}>
                        <Typography variant='caption'>{t('views.scheduler.from')}</Typography>
                        <FormControl size='small' sx={{ minWidth:160 }}>
                          <InputLabel id='weeklyStartDay'>{t('views.scheduler.day')}</InputLabel>
                          <Select
                            labelId='weeklyStartDay'
                            label={t('views.scheduler.day')}
                            value={startWeekValue === 'WWW' ? '' : startWeekValue}
                            onChange={(e) => editTimestamp(e.target.value, 'week')}
                          >
                            <MenuItem value='' disabled><em>{t('views.scheduler.day')}</em></MenuItem>
                            {daysWeek.map(day => (
                              <MenuItem key={day} value={day.slice(0,3)}>{day}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        <Typography variant='caption'>{t('views.scheduler.at')}</Typography>
                        <Box sx={{ display:'flex', gap:'12px', alignItems:'center' }}>
                          <FormControl size='small' sx={{ minWidth:90 }}>
                            <InputLabel id='weeklyStartHour'>{t('views.scheduler.hour')}</InputLabel>
                            <Select
                              labelId='weeklyStartHour'
                              label={t('views.scheduler.hour')}
                              value={startHourDisplayValue}
                              onChange={(e) => {
                                const hourVal = parseInt(e.target.value, 10);
                                const resolvedHour = is24HourFormat ? hourVal : to24Hour(hourVal, startMeridiemValue);
                                const minuteVal = isFiniteNumber(startMinuteValue) ? startMinuteValue : 0;
                                setTimestampTime(editTimestamp, resolvedHour, minuteVal);
                              }}
                              disabled={startWeekValue === 'WWW'}
                            >
                              {is24HourFormat
                                ? Array.from({ length:24 }, (_, i) => (
                                    <MenuItem key={i} value={i}>{String(i).padStart(2,'0')}</MenuItem>
                                  ))
                                : Array.from({ length:12 }, (_, i) => (
                                    <MenuItem key={i + 1} value={i + 1}>{String(i + 1).padStart(2,'0')}</MenuItem>
                                  ))}
                            </Select>
                          </FormControl>
                          <Typography>:</Typography>
                          <FormControl size='small' sx={{ minWidth:90 }}>
                            <InputLabel id='weeklyStartMinute'>{t('views.scheduler.minute')}</InputLabel>
                            <Select
                              labelId='weeklyStartMinute'
                              label={t('views.scheduler.minute')}
                              value={isFiniteNumber(startMinuteValue) ? startMinuteValue : ''}
                              onChange={(e) => {
                                const minuteVal = parseInt(e.target.value, 10);
                                const hourVal = isFiniteNumber(startHourValue) ? startHourValue : 0;
                                setTimestampTime(editTimestamp, hourVal, minuteVal);
                              }}
                              disabled={!isFiniteNumber(startHourValue)}
                            >
                              {Array.from({ length:60 }, (_, i) => (
                                <MenuItem key={i} value={i}>{String(i).padStart(2,'0')}</MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                          {!is24HourFormat && (
                            <FormControl size='small' sx={{ minWidth:90 }}>
                              <InputLabel id='weeklyStartMeridiem'>AM/PM</InputLabel>
                              <Select
                                labelId='weeklyStartMeridiem'
                                label='AM/PM'
                                value={startMeridiemValue}
                                onChange={(e) => {
                                  if(!isFiniteNumber(startHourDisplayValue)) return;
                                  const resolvedHour = to24Hour(startHourDisplayValue, e.target.value);
                                  const minuteVal = isFiniteNumber(startMinuteValue) ? startMinuteValue : 0;
                                  setTimestampTime(editTimestamp, resolvedHour, minuteVal);
                                }}
                                disabled={!isFiniteNumber(startHourDisplayValue)}
                              >
                                <MenuItem value='AM'>AM</MenuItem>
                                <MenuItem value='PM'>PM</MenuItem>
                              </Select>
                            </FormControl>
                          )}
                        </Box>
                      </Box>
                      <ArrowForwardIcon />
                      <Box sx={{ display:'flex', flexDirection:'column', gap:'12px', alignItems:'center' }}>
                        <Typography variant='caption'>{t('views.scheduler.to')}</Typography>
                        <FormControl size='small' sx={{ minWidth:160 }}>
                          <InputLabel id='weeklyStopDay'>{t('views.scheduler.day')}</InputLabel>
                          <Select
                            labelId='weeklyStopDay'
                            label={t('views.scheduler.day')}
                            value={stopWeekValue === 'WWW' ? '' : stopWeekValue}
                            onChange={(e) => editTimestampTo(e.target.value, 'week')}
                            disabled={startWeekValue === 'WWW'}
                          >
                            <MenuItem value='' disabled><em>{t('views.scheduler.day')}</em></MenuItem>
                            {weeklyStopOptions.map(day => (
                              <MenuItem key={day} value={day.slice(0,3)}>{day}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        <Typography variant='caption'>{t('views.scheduler.at')}</Typography>
                        <Box sx={{ display:'flex', gap:'12px', alignItems:'center' }}>
                          <FormControl size='small' sx={{ minWidth:90 }}>
                            <InputLabel id='weeklyStopHour'>{t('views.scheduler.hour')}</InputLabel>
                            <Select
                              labelId='weeklyStopHour'
                              label={t('views.scheduler.hour')}
                              value={stopHourDisplayValue}
                              onChange={(e) => {
                                const hourVal = parseInt(e.target.value, 10);
                                const resolvedHour = is24HourFormat ? hourVal : to24Hour(hourVal, stopMeridiemValue);
                                const minuteVal = isFiniteNumber(stopMinuteValue) ? stopMinuteValue : 0;
                                setTimestampTime(editTimestampTo, resolvedHour, minuteVal);
                              }}
                              disabled={stopWeekValue === 'WWW'}
                            >
                              {is24HourFormat
                                ? Array.from({ length:24 }, (_, i) => (
                                    <MenuItem key={i} value={i}>{String(i).padStart(2,'0')}</MenuItem>
                                  ))
                                : Array.from({ length:12 }, (_, i) => (
                                    <MenuItem key={i + 1} value={i + 1}>{String(i + 1).padStart(2,'0')}</MenuItem>
                                  ))}
                            </Select>
                          </FormControl>
                          <Typography>:</Typography>
                          <FormControl size='small' sx={{ minWidth:90 }}>
                            <InputLabel id='weeklyStopMinute'>{t('views.scheduler.minute')}</InputLabel>
                            <Select
                              labelId='weeklyStopMinute'
                              label={t('views.scheduler.minute')}
                              value={isFiniteNumber(stopMinuteValue) ? stopMinuteValue : ''}
                              onChange={(e) => {
                                if(!isFiniteNumber(stopHourValue)) return;
                                const minuteVal = parseInt(e.target.value, 10);
                                const sameDay = startWeekValue !== 'WWW' && startWeekValue === stopWeekValue;
                                if(sameDay && isFiniteNumber(startHourValue) && isFiniteNumber(startMinuteValue)) {
                                  const startTotal = getTotalMinutes(startHourValue, startMinuteValue);
                                  const candidate = getTotalMinutes(stopHourValue, minuteVal);
                                  if((candidate - startTotal) < MIN_DIFF_MINUTES) {
                                    toast.error(t('views.scheduler.minHourDiff'));
                                    return;
                                  }
                                }
                                setTimestampTime(editTimestampTo, stopHourValue, minuteVal);
                              }}
                              disabled={!isFiniteNumber(stopHourValue)}
                            >
                              {Array.from({ length:60 }, (_, i) => (
                                <MenuItem key={i} value={i}>{String(i).padStart(2,'0')}</MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                          {!is24HourFormat && (
                            <FormControl size='small' sx={{ minWidth:90 }}>
                              <InputLabel id='weeklyStopMeridiem'>AM/PM</InputLabel>
                              <Select
                                labelId='weeklyStopMeridiem'
                                label='AM/PM'
                                value={stopMeridiemValue}
                                onChange={(e) => {
                                  if(!isFiniteNumber(stopHourDisplayValue)) return;
                                  const resolvedHour = to24Hour(stopHourDisplayValue, e.target.value);
                                  const minuteVal = isFiniteNumber(stopMinuteValue) ? stopMinuteValue : 0;
                                  const sameDay = startWeekValue !== 'WWW' && startWeekValue === stopWeekValue;
                                  if(sameDay && isFiniteNumber(startHourValue) && isFiniteNumber(startMinuteValue)) {
                                    const startTotal = getTotalMinutes(startHourValue, startMinuteValue);
                                    const candidate = getTotalMinutes(resolvedHour, minuteVal);
                                    if((candidate - startTotal) < MIN_DIFF_MINUTES) {
                                      toast.error(t('views.scheduler.minHourDiff'));
                                      return;
                                    }
                                  }
                                  setTimestampTime(editTimestampTo, resolvedHour, minuteVal);
                                }}
                                disabled={!isFiniteNumber(stopHourDisplayValue)}
                              >
                                <MenuItem value='AM'>AM</MenuItem>
                                <MenuItem value='PM'>PM</MenuItem>
                              </Select>
                            </FormControl>
                          )}
                        </Box>
                      </Box>
                    </Box>
                    {canSelectContent() && (
                      <Box sx={{ mt:3 }}>
                        <ContentSelect isRepeatable={true} />
                      </Box>
                    )}
                  </>
                )}
                {repeat === 'monthly' && (
                  <>
                    <Box sx={{ display:'flex', justifyContent:'center', gap:'30px', flexWrap:'wrap' }}>
                      <Box sx={{ display:'flex', flexDirection:'column', gap:'12px', alignItems:'center' }}>
                        <Typography variant='caption'>{t('views.scheduler.from')}</Typography>
                        <FormControl size='small' sx={{ minWidth:140 }}>
                          <InputLabel id='monthlyStartDay'>{t('views.scheduler.dayOfMonth')}</InputLabel>
                          <Select
                            labelId='monthlyStartDay'
                            label={t('views.scheduler.dayOfMonth')}
                            value={startDayValue === 'DD' ? '' : startDayValue}
                            onChange={(e) => editTimestamp(e.target.value, 'day')}
                          >
                            <MenuItem value='' disabled><em>{t('views.scheduler.dayOfMonth')}</em></MenuItem>
                            {daysOfMonth.map(day => (
                              <MenuItem key={day} value={day}>{day}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        <Typography variant='caption'>{t('views.scheduler.dayOfMonthAt')}</Typography>
                        <Box sx={{ display:'flex', gap:'12px', alignItems:'center' }}>
                          <FormControl size='small' sx={{ minWidth:90 }}>
                            <InputLabel id='monthlyStartHour'>{t('views.scheduler.hour')}</InputLabel>
                            <Select
                              labelId='monthlyStartHour'
                              label={t('views.scheduler.hour')}
                              value={startHourDisplayValue}
                              onChange={(e) => {
                                const hourVal = parseInt(e.target.value, 10);
                                const resolvedHour = is24HourFormat ? hourVal : to24Hour(hourVal, startMeridiemValue);
                                const minuteVal = isFiniteNumber(startMinuteValue) ? startMinuteValue : 0;
                                setTimestampTime(editTimestamp, resolvedHour, minuteVal);
                              }}
                              disabled={startDayValue === 'DD'}
                            >
                              {is24HourFormat
                                ? Array.from({ length:24 }, (_, i) => (
                                    <MenuItem key={i} value={i}>{String(i).padStart(2,'0')}</MenuItem>
                                  ))
                                : Array.from({ length:12 }, (_, i) => (
                                    <MenuItem key={i + 1} value={i + 1}>{String(i + 1).padStart(2,'0')}</MenuItem>
                                  ))}
                            </Select>
                          </FormControl>
                          <Typography>:</Typography>
                          <FormControl size='small' sx={{ minWidth:90 }}>
                            <InputLabel id='monthlyStartMinute'>{t('views.scheduler.minute')}</InputLabel>
                            <Select
                              labelId='monthlyStartMinute'
                              label={t('views.scheduler.minute')}
                              value={isFiniteNumber(startMinuteValue) ? startMinuteValue : ''}
                              onChange={(e) => {
                                const minuteVal = parseInt(e.target.value, 10);
                                const hourVal = isFiniteNumber(startHourValue) ? startHourValue : 0;
                                setTimestampTime(editTimestamp, hourVal, minuteVal);
                              }}
                              disabled={!isFiniteNumber(startHourValue)}
                            >
                              {Array.from({ length:60 }, (_, i) => (
                                <MenuItem key={i} value={i}>{String(i).padStart(2,'0')}</MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                          {!is24HourFormat && (
                            <FormControl size='small' sx={{ minWidth:90 }}>
                              <InputLabel id='monthlyStartMeridiem'>AM/PM</InputLabel>
                              <Select
                                labelId='monthlyStartMeridiem'
                                label='AM/PM'
                                value={startMeridiemValue}
                                onChange={(e) => {
                                  if(!isFiniteNumber(startHourDisplayValue)) return;
                                  const resolvedHour = to24Hour(startHourDisplayValue, e.target.value);
                                  const minuteVal = isFiniteNumber(startMinuteValue) ? startMinuteValue : 0;
                                  setTimestampTime(editTimestamp, resolvedHour, minuteVal);
                                }}
                                disabled={!isFiniteNumber(startHourDisplayValue)}
                              >
                                <MenuItem value='AM'>AM</MenuItem>
                                <MenuItem value='PM'>PM</MenuItem>
                              </Select>
                            </FormControl>
                          )}
                        </Box>
                      </Box>
                      <ArrowForwardIcon />
                      <Box sx={{ display:'flex', flexDirection:'column', gap:'12px', alignItems:'center' }}>
                        <Typography variant='caption'>{t('views.scheduler.to')}</Typography>
                        <FormControl size='small' sx={{ minWidth:140 }}>
                          <InputLabel id='monthlyStopDay'>{t('views.scheduler.dayOfMonth')}</InputLabel>
                          <Select
                            labelId='monthlyStopDay'
                            label={t('views.scheduler.dayOfMonth')}
                            value={stopDayValue === 'DD' ? '' : stopDayValue}
                            onChange={(e) => editTimestampTo(e.target.value, 'day')}
                            disabled={startDayValue === 'DD'}
                          >
                            <MenuItem value='' disabled><em>{t('views.scheduler.dayOfMonth')}</em></MenuItem>
                            {monthlyStopOptions.map(day => (
                              <MenuItem key={day} value={day}>{day}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        <Typography variant='caption'>{t('views.scheduler.dayOfMonthAt')}</Typography>
                        <Box sx={{ display:'flex', gap:'12px', alignItems:'center' }}>
                          <FormControl size='small' sx={{ minWidth:90 }}>
                            <InputLabel id='monthlyStopHour'>{t('views.scheduler.hour')}</InputLabel>
                            <Select
                              labelId='monthlyStopHour'
                              label={t('views.scheduler.hour')}
                              value={stopHourDisplayValue}
                              onChange={(e) => {
                                const hourVal = parseInt(e.target.value, 10);
                                const resolvedHour = is24HourFormat ? hourVal : to24Hour(hourVal, stopMeridiemValue);
                                const minuteVal = isFiniteNumber(stopMinuteValue) ? stopMinuteValue : 0;
                                setTimestampTime(editTimestampTo, resolvedHour, minuteVal);
                              }}
                              disabled={stopDayValue === 'DD'}
                            >
                              {is24HourFormat
                                ? Array.from({ length:24 }, (_, i) => (
                                    <MenuItem key={i} value={i}>{String(i).padStart(2,'0')}</MenuItem>
                                  ))
                                : Array.from({ length:12 }, (_, i) => (
                                    <MenuItem key={i + 1} value={i + 1}>{String(i + 1).padStart(2,'0')}</MenuItem>
                                  ))}
                            </Select>
                          </FormControl>
                          <Typography>:</Typography>
                          <FormControl size='small' sx={{ minWidth:90 }}>
                            <InputLabel id='monthlyStopMinute'>{t('views.scheduler.minute')}</InputLabel>
                            <Select
                              labelId='monthlyStopMinute'
                              label={t('views.scheduler.minute')}
                              value={isFiniteNumber(stopMinuteValue) ? stopMinuteValue : ''}
                              onChange={(e) => {
                                if(!isFiniteNumber(stopHourValue)) return;
                                const minuteVal = parseInt(e.target.value, 10);
                                const sameDay = startDayValue !== 'DD' && startDayValue === stopDayValue;
                                if(sameDay && isFiniteNumber(startHourValue) && isFiniteNumber(startMinuteValue)) {
                                  const startTotal = getTotalMinutes(startHourValue, startMinuteValue);
                                  const candidate = getTotalMinutes(stopHourValue, minuteVal);
                                  if((candidate - startTotal) < MIN_DIFF_MINUTES) {
                                    toast.error(t('views.scheduler.minHourDiff'));
                                    return;
                                  }
                                }
                                setTimestampTime(editTimestampTo, stopHourValue, minuteVal);
                              }}
                              disabled={!isFiniteNumber(stopHourValue)}
                            >
                              {Array.from({ length:60 }, (_, i) => (
                                <MenuItem key={i} value={i}>{String(i).padStart(2,'0')}</MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                          {!is24HourFormat && (
                            <FormControl size='small' sx={{ minWidth:90 }}>
                              <InputLabel id='monthlyStopMeridiem'>AM/PM</InputLabel>
                              <Select
                                labelId='monthlyStopMeridiem'
                                label='AM/PM'
                                value={stopMeridiemValue}
                                onChange={(e) => {
                                  if(!isFiniteNumber(stopHourDisplayValue)) return;
                                  const resolvedHour = to24Hour(stopHourDisplayValue, e.target.value);
                                  const minuteVal = isFiniteNumber(stopMinuteValue) ? stopMinuteValue : 0;
                                  const sameDay = startDayValue !== 'DD' && startDayValue === stopDayValue;
                                  if(sameDay && isFiniteNumber(startHourValue) && isFiniteNumber(startMinuteValue)) {
                                    const startTotal = getTotalMinutes(startHourValue, startMinuteValue);
                                    const candidate = getTotalMinutes(resolvedHour, minuteVal);
                                    if((candidate - startTotal) < MIN_DIFF_MINUTES) {
                                      toast.error(t('views.scheduler.minHourDiff'));
                                      return;
                                    }
                                  }
                                  setTimestampTime(editTimestampTo, resolvedHour, minuteVal);
                                }}
                                disabled={!isFiniteNumber(stopHourDisplayValue)}
                              >
                                <MenuItem value='AM'>AM</MenuItem>
                                <MenuItem value='PM'>PM</MenuItem>
                              </Select>
                            </FormControl>
                          )}
                        </Box>
                      </Box>
                    </Box>
                    {canSelectContent() && (
                      <Box sx={{ mt:3 }}>
                        <ContentSelect isRepeatable={true} />
                      </Box>
                    )}
                  </>
                )}
                {repeat === 'yearly' && (
                  <>
                    <Box sx={{ display:'flex', justifyContent:'center', gap:'30px', flexWrap:'wrap' }}>
                      <Box sx={{ display:'flex', flexDirection:'column', gap:'12px', alignItems:'center' }}>
                        <Typography variant='caption'>{t('views.scheduler.from')}</Typography>
                        <FormControl size='small' sx={{ minWidth:160 }}>
                          <InputLabel id='yearlyStartMonth'>{t('views.scheduler.monthLower')}</InputLabel>
                          <Select
                            labelId='yearlyStartMonth'
                            label={t('views.scheduler.monthLower')}
                            value={startMonthValue === 'MMM' ? '' : startMonthValue}
                            onChange={(e) => editTimestamp(e.target.value, 'month')}
                          >
                            <MenuItem value='' disabled><em>{t('views.scheduler.monthLower')}</em></MenuItem>
                            {months.map(month => (
                              <MenuItem key={month} value={month.slice(0,3)}>{month}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        <Typography variant='caption'>{t('views.scheduler.on')}</Typography>
                        <FormControl size='small' sx={{ minWidth:140 }}>
                          <InputLabel id='yearlyStartDay'>{t('views.scheduler.dayOfMonth')}</InputLabel>
                          <Select
                            labelId='yearlyStartDay'
                            label={t('views.scheduler.dayOfMonth')}
                            value={startDayValue === 'DD' ? '' : startDayValue}
                            onChange={(e) => editTimestamp(e.target.value, 'day')}
                            disabled={startMonthValue === 'MMM'}
                          >
                            <MenuItem value='' disabled><em>{t('views.scheduler.dayOfMonth')}</em></MenuItem>
                            {daysOfMonth.map(day => (
                              <MenuItem key={day} value={day}>{day}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        <Typography variant='caption'>{t('views.scheduler.dayOfMonthAt')}</Typography>
                        <Box sx={{ display:'flex', gap:'12px', alignItems:'center' }}>
                          <FormControl size='small' sx={{ minWidth:90 }}>
                            <InputLabel id='yearlyStartHour'>{t('views.scheduler.hour')}</InputLabel>
                            <Select
                              labelId='yearlyStartHour'
                              label={t('views.scheduler.hour')}
                              value={startHourDisplayValue}
                              onChange={(e) => {
                                const hourVal = parseInt(e.target.value, 10);
                                const resolvedHour = is24HourFormat ? hourVal : to24Hour(hourVal, startMeridiemValue);
                                const minuteVal = isFiniteNumber(startMinuteValue) ? startMinuteValue : 0;
                                setTimestampTime(editTimestamp, resolvedHour, minuteVal);
                              }}
                              disabled={startDayValue === 'DD'}
                            >
                              {is24HourFormat
                                ? Array.from({ length:24 }, (_, i) => (
                                    <MenuItem key={i} value={i}>{String(i).padStart(2,'0')}</MenuItem>
                                  ))
                                : Array.from({ length:12 }, (_, i) => (
                                    <MenuItem key={i + 1} value={i + 1}>{String(i + 1).padStart(2,'0')}</MenuItem>
                                  ))}
                            </Select>
                          </FormControl>
                          <Typography>:</Typography>
                          <FormControl size='small' sx={{ minWidth:90 }}>
                            <InputLabel id='yearlyStartMinute'>{t('views.scheduler.minute')}</InputLabel>
                            <Select
                              labelId='yearlyStartMinute'
                              label={t('views.scheduler.minute')}
                              value={isFiniteNumber(startMinuteValue) ? startMinuteValue : ''}
                              onChange={(e) => {
                                const minuteVal = parseInt(e.target.value, 10);
                                const hourVal = isFiniteNumber(startHourValue) ? startHourValue : 0;
                                setTimestampTime(editTimestamp, hourVal, minuteVal);
                              }}
                              disabled={!isFiniteNumber(startHourValue)}
                            >
                              {Array.from({ length:60 }, (_, i) => (
                                <MenuItem key={i} value={i}>{String(i).padStart(2,'0')}</MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                          {!is24HourFormat && (
                            <FormControl size='small' sx={{ minWidth:90 }}>
                              <InputLabel id='yearlyStartMeridiem'>AM/PM</InputLabel>
                              <Select
                                labelId='yearlyStartMeridiem'
                                label='AM/PM'
                                value={startMeridiemValue}
                                onChange={(e) => {
                                  if(!isFiniteNumber(startHourDisplayValue)) return;
                                  const resolvedHour = to24Hour(startHourDisplayValue, e.target.value);
                                  const minuteVal = isFiniteNumber(startMinuteValue) ? startMinuteValue : 0;
                                  setTimestampTime(editTimestamp, resolvedHour, minuteVal);
                                }}
                                disabled={!isFiniteNumber(startHourDisplayValue)}
                              >
                                <MenuItem value='AM'>AM</MenuItem>
                                <MenuItem value='PM'>PM</MenuItem>
                              </Select>
                            </FormControl>
                          )}
                        </Box>
                      </Box>
                      <ArrowForwardIcon />
                      <Box sx={{ display:'flex', flexDirection:'column', gap:'12px', alignItems:'center' }}>
                        <Typography variant='caption'>{t('views.scheduler.to')}</Typography>
                        <FormControl size='small' sx={{ minWidth:160 }}>
                          <InputLabel id='yearlyStopMonth'>{t('views.scheduler.monthLower')}</InputLabel>
                          <Select
                            labelId='yearlyStopMonth'
                            label={t('views.scheduler.monthLower')}
                            value={stopMonthValue === 'MMM' ? '' : stopMonthValue}
                            onChange={(e) => editTimestampTo(e.target.value, 'month')}
                            disabled={startMonthValue === 'MMM'}
                          >
                            <MenuItem value='' disabled><em>{t('views.scheduler.monthLower')}</em></MenuItem>
                            {yearlyStopMonthOptions.map(month => (
                              <MenuItem key={month} value={month.slice(0,3)}>{month}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        <Typography variant='caption'>{t('views.scheduler.on')}</Typography>
                        <FormControl size='small' sx={{ minWidth:140 }}>
                          <InputLabel id='yearlyStopDay'>{t('views.scheduler.dayOfMonth')}</InputLabel>
                          <Select
                            labelId='yearlyStopDay'
                            label={t('views.scheduler.dayOfMonth')}
                            value={stopDayValue === 'DD' ? '' : stopDayValue}
                            onChange={(e) => editTimestampTo(e.target.value, 'day')}
                            disabled={stopMonthValue === 'MMM'}
                          >
                            <MenuItem value='' disabled><em>{t('views.scheduler.dayOfMonth')}</em></MenuItem>
                            {yearlyStopDayOptions.map(day => (
                              <MenuItem key={day} value={day}>{day}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        <Typography variant='caption'>{t('views.scheduler.dayOfMonthAt')}</Typography>
                        <Box sx={{ display:'flex', gap:'12px', alignItems:'center' }}>
                          <FormControl size='small' sx={{ minWidth:90 }}>
                            <InputLabel id='yearlyStopHour'>{t('views.scheduler.hour')}</InputLabel>
                            <Select
                              labelId='yearlyStopHour'
                              label={t('views.scheduler.hour')}
                              value={stopHourDisplayValue}
                              onChange={(e) => {
                                const hourVal = parseInt(e.target.value, 10);
                                const resolvedHour = is24HourFormat ? hourVal : to24Hour(hourVal, stopMeridiemValue);
                                const minuteVal = isFiniteNumber(stopMinuteValue) ? stopMinuteValue : 0;
                                setTimestampTime(editTimestampTo, resolvedHour, minuteVal);
                              }}
                              disabled={stopDayValue === 'DD'}
                            >
                              {is24HourFormat
                                ? Array.from({ length:24 }, (_, i) => (
                                    <MenuItem key={i} value={i}>{String(i).padStart(2,'0')}</MenuItem>
                                  ))
                                : Array.from({ length:12 }, (_, i) => (
                                    <MenuItem key={i + 1} value={i + 1}>{String(i + 1).padStart(2,'0')}</MenuItem>
                                  ))}
                            </Select>
                          </FormControl>
                          <Typography>:</Typography>
                          <FormControl size='small' sx={{ minWidth:90 }}>
                            <InputLabel id='yearlyStopMinute'>{t('views.scheduler.minute')}</InputLabel>
                            <Select
                              labelId='yearlyStopMinute'
                              label={t('views.scheduler.minute')}
                              value={isFiniteNumber(stopMinuteValue) ? stopMinuteValue : ''}
                              onChange={(e) => {
                                if(!isFiniteNumber(stopHourValue)) return;
                                const minuteVal = parseInt(e.target.value, 10);
                                const sameDay = startMonthValue !== 'MMM' && startMonthValue === stopMonthValue && startDayValue !== 'DD' && startDayValue === stopDayValue;
                                if(sameDay && isFiniteNumber(startHourValue) && isFiniteNumber(startMinuteValue)) {
                                  const startTotal = getTotalMinutes(startHourValue, startMinuteValue);
                                  const candidate = getTotalMinutes(stopHourValue, minuteVal);
                                  if((candidate - startTotal) < MIN_DIFF_MINUTES) {
                                    toast.error(t('views.scheduler.minHourDiff'));
                                    return;
                                  }
                                }
                                setTimestampTime(editTimestampTo, stopHourValue, minuteVal);
                              }}
                              disabled={!isFiniteNumber(stopHourValue)}
                            >
                              {Array.from({ length:60 }, (_, i) => (
                                <MenuItem key={i} value={i}>{String(i).padStart(2,'0')}</MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                          {!is24HourFormat && (
                            <FormControl size='small' sx={{ minWidth:90 }}>
                              <InputLabel id='yearlyStopMeridiem'>AM/PM</InputLabel>
                              <Select
                                labelId='yearlyStopMeridiem'
                                label='AM/PM'
                                value={stopMeridiemValue}
                                onChange={(e) => {
                                  if(!isFiniteNumber(stopHourDisplayValue)) return;
                                  const resolvedHour = to24Hour(stopHourDisplayValue, e.target.value);
                                  const minuteVal = isFiniteNumber(stopMinuteValue) ? stopMinuteValue : 0;
                                  const sameDay = startMonthValue !== 'MMM' && startMonthValue === stopMonthValue && startDayValue !== 'DD' && startDayValue === stopDayValue;
                                  if(sameDay && isFiniteNumber(startHourValue) && isFiniteNumber(startMinuteValue)) {
                                    const startTotal = getTotalMinutes(startHourValue, startMinuteValue);
                                    const candidate = getTotalMinutes(resolvedHour, minuteVal);
                                    if((candidate - startTotal) < MIN_DIFF_MINUTES) {
                                      toast.error(t('views.scheduler.minHourDiff'));
                                      return;
                                    }
                                  }
                                  setTimestampTime(editTimestampTo, resolvedHour, minuteVal);
                                }}
                                disabled={!isFiniteNumber(stopHourDisplayValue)}
                              >
                                <MenuItem value='AM'>AM</MenuItem>
                                <MenuItem value='PM'>PM</MenuItem>
                              </Select>
                            </FormControl>
                          )}
                        </Box>
                      </Box>
                    </Box>
                    {canSelectContent() && (
                      <Box sx={{ mt:3 }}>
                        <ContentSelect isRepeatable={true} />
                      </Box>
                    )}
                  </>
                )}
              </>
            )}
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AddEventDialogMinute;
